import { useMutation, useQuery } from "@tanstack/react-query";

import { useAppState } from "../../../../../context/StateContext";
import { Filters, Searches } from "../../../../../models/shipeezi.table";
import DespatchService from "../../../../../services/despatch.service";
import { DespatchKeys } from "../../../../../util/queries";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour

const useAction = () => {
  const { currentUser } = useAppState();

  const despatchListAttributes = [
    "id",
    "status",
    "originId",
    "destinationId",
    "createdAt",
    "pickupDate",
    "deliveryDate",
    "externalUuid",
  ];
  const addressAttributes = [
    "addressLine1",
    "addressLine2",
    "country",
    "suburb",
    "longAddress",
    "latitude",
    "longitude",
    "postalcode",
    "name",
  ];

  const getAllDespatches = (
    page: number = 1,
    pageSize: number = 20,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: DespatchKeys.getAndCountAll([page, pageSize, searches, filters]),
      queryFn: async () => {
        const response = await DespatchService.getAndCountAll({
          page,
          pageSize,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          attributes: despatchListAttributes,
          include: JSON.stringify([
            {
              association: "originAddress",
              attributes: addressAttributes,
            },
            {
              association: "destinationAddress",
              attributes: addressAttributes,
            },
          ]),
          ...filters,
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.id,
      staleTime: 3 * 60 * 1000, // 3 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const usePrintDeliveryDocket = () => {
    return useMutation({
      mutationKey: DespatchKeys.printDeliveryDocket(),
      mutationFn: (id: number) => DespatchService.printDeliveryDocket(id),
      onSuccess: (response) => {
        if (response) {
          const file = new Blob([new Uint8Array(response?.data?.content).buffer], {
            type: "application/pdf",
          });

          const fileURL = URL.createObjectURL(file);

          window.open(fileURL, "_blank")?.focus();
        }
      },
    });
  };

  return {
    getAllDespatches,
    usePrintDeliveryDocket,
  };
};

export default useAction;
